<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import ReorderableTable from '@/components/ReorderableTable.vue';
import SummaryTableWrapper from '@/components/ConsultationSummary/SummaryTableWrapper.vue';

import { useSummariesStore } from '@/stores/summaries.store';
import type { Locales, Pagination, SummaryFormOtherChoiceDto } from '@/services/types/api.types';

// STORE
const summariesStore = useSummariesStore();
const { formOtherChoices } = storeToRefs(summariesStore);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  formId: number;
}>();

// CONST
const { t } = useI18n();

// COMPUTED
const columns = computed(() => [
  {
    name: 'answer',
    label: t('form.summary.results.other-choices'),
    align: 'left',
    field: (row: SummaryFormOtherChoiceDto) => row.text,
  },
]);

const hasOtherChoices = computed(() => {
  const choices = formOtherChoices?.value?.get(props.formId);
  return choices?.data && choices.data.length > 0;
});

// FUNCTIONS
const fetchOtherChoices = async (request: Pagination) => {
  await summariesStore.getFormOtherChoices(props.formId, request.page, request.rowsPerPage);
};
</script>
<template>
  <div class="form-other-results">
    <div v-if="hasOtherChoices">
      <summary-table-wrapper>
        <template v-slot>
          <reorderable-table
            :rows="formOtherChoices.get(props.formId)?.data || []"
            :columns="columns"
            :loading="summariesStore.isFormOtherChoicesLoading(props.formId)"
            itemKey="id"
            :disable-add-button="true"
            :disable-delete-button="true"
            :disableOrderable="true"
            :pagination-enabled="true"
            :pagination="formOtherChoices.get(props.formId)?.pagination"
            @request="fetchOtherChoices"
          >
            <template v-slot:body-cell-answer="props">
              <q-td :props="props">
                <div class="text">
                  {{ props.row.text }}
                </div>
              </q-td>
            </template>
          </reorderable-table>
        </template>
      </summary-table-wrapper>
    </div>
    <div v-else class="form-other-results__no-other-choices">
      <div class="title">
        {{ t('form.summary.results.other-choices') }}
      </div>
      <div class="text">
        {{ t('form.summary.status.no-other-choices') }}
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.form-other-results {
  .text {
    font-size: $small-text;
    color: $color-neutral-grey-400;
    margin: $space-sm;
  }

  &__no-other-choices {
    .title {
      font-weight: bold;
      font-size: $sub-header;
      margin-bottom: $space-sm;
      padding: 0 !important;
    }

    .text {
      color: $color-neutral-grey-400;
      font-size: $small-text;
      padding: 0 0 $space-md 0;
    }
  }

  tbody tr:nth-child(even) {
    background-color: $color-neutral-white;
  }

  tbody tr:nth-child(odd) {
    background-color: $color-neutral-grey-200;

    td {
      border-radius: 6px;
    }
  }
}
</style>
